import { useEffect, useState } from 'react';
import {
    AlertBannerType,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExButton,
    ExLoader,
} from '@boomi/exosphere';
import {
    type IdentityProvider,
    fetchIdentityProviders,
} from '../identity-providers/identityProviders';
import { getConnector, type Connector } from '../connectors/connectors';
import { getConnection, updateConnection, deleteConnection } from './connections';
import ConnectionFormFields, { type FormValues, EMPTY_FORM_VALUES } from './ConnectionFormFields';
import ConnectorSidebar from './ConnectorSidebar';
import styles from '../shared/Form.module.css';
import connectionStyles from './Connection.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

interface ConnectionEditProps {
    connectorId: string;
    connectionId: string;
    onClose: () => void;
}

const ConnectionEdit = ({ connectorId, connectionId, onClose }: ConnectionEditProps) => {
    const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES);
    const [connector, setConnector] = useState<Connector | null>(null);
    const [identityProviders, setIdentityProviders] = useState<IdentityProvider[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [deleteState, setDeleteState] = useState<'idle' | 'confirming'>('idle');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        Promise.all([
            getConnection(connectionId),
            fetchIdentityProviders(),
            getConnector(connectorId),
        ])
            .then(([connection, idpResponse, connectorData]) => {
                setIdentityProviders(idpResponse.results);
                setConnector(connectorData);
                setValues({
                    name: connection.name,
                    identityProviderId: connection.identityProviderId,
                    clientId: connection.clientId,
                    enabled: connection.enabled,
                    // userOptions are not yet returned by the API
                    userOptions: {},
                });
            })
            .catch(() => setLoadError('Failed to load connection.'))
            .finally(() => setLoading(false));
    }, [connectionId]);

    const handleChange = (patch: Partial<FormValues>) =>
        setValues((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setSubmitError(null);
        setSubmitting(true);

        try {
            await updateConnection(connectionId, {
                name: values.name,
                identityProviderId: values.identityProviderId,
                connectorId,
                options: values.userOptions,
                enabled: values.enabled,
                accountOwned: true,
            });
            onClose();
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    const deleteConfirm = !loading && !loadError && deleteState === 'confirming' && (
        <div className={styles.deleteConfirmRow}>
            <span className={styles.deleteConfirmText}>Remove this config?</span>
            <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setDeleteState('idle')}
            >
                No, keep
            </button>
            <ExButton
                type={ButtonType.PRIMARY}
                flavor={ButtonFlavor.RISKY}
                disabled={isDeleting}
                onClick={async () => {
                    setIsDeleting(true);
                    try {
                        await deleteConnection(connectionId);
                        onClose();
                    } catch (err) {
                        setIsDeleting(false);
                        setDeleteState('idle');
                        setSubmitError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
                    }
                }}
            >
                {isDeleting ? 'Deleting…' : 'Yes, remove'}
            </ExButton>
        </div>
    );

    return (
        <>
            <div className={connectionStyles.drawerLayout}>
                {connector && <ConnectorSidebar connector={connector} />}
                <div className={connectionStyles.formColumn}>
                    {loadError ? (
                        <ExAlertBanner type={AlertBannerType.ERROR} open>
                            {loadError}
                        </ExAlertBanner>
                    ) : loading ? (
                        <ExLoader label="Loading connection…" />
                    ) : (
                        <form className={styles.form} onSubmit={handleSubmit}>
                            {connector && (
                                <ConnectionFormFields
                                    values={values}
                                    identityProviders={identityProviders}
                                    isLoadingIdentityProviders={loading}
                                    onChange={handleChange}
                                    isEdit
                                    userAuthOptions={connector.userAuthOptions}
                                    connectorId={connectorId}
                                    connectorName={connector.name}
                                />
                            )}

                            {submitError && (
                                <ExAlertBanner
                                    className={styles.saveError}
                                    type={AlertBannerType.ERROR}
                                    open
                                    onClose={() => setSubmitError(null)}
                                >
                                    {submitError}
                                </ExAlertBanner>
                            )}
                        </form>
                    )}
                </div>
            </div>
            <div className={styles.actions} slot="footer">
                {deleteState === 'idle' ? (
                    <>
                        <ExButton
                            type={ButtonType.SECONDARY}
                            flavor={ButtonFlavor.RISKY}
                            onClick={() => setDeleteState('confirming')}
                        >
                            Delete
                        </ExButton>
                        <ExButton
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            disabled={submitting}
                            onClick={() => handleSubmit()}
                        >
                            {submitting ? 'Saving…' : 'Save changes'}
                        </ExButton>
                    </>
                ) : (
                    deleteConfirm
                )}
            </div>
        </>
    );
};

export default ConnectionEdit;
